import React from 'react';
import { Loader2, Wallet } from 'lucide-react';
import type { StudentFeeLedger } from '../profile.types';

interface Props {
  fees: StudentFeeLedger | null;
  loading: boolean;
}

const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

const FeesTab: React.FC<Props> = ({ fees, loading }) => {
  if (loading && !fees) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--slate)' }} /></div>;
  }

  const installments = fees?.installments ?? [];
  const sum = fees?.summary;

  if (!installments.length) {
    return (
      <div className="empty">
        <div className="empty-art"><Wallet size={40} /></div>
        <h3>No fees assigned</h3>
        <p>Fee installments for this student will appear here once set up.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      {sum && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 22, flexWrap: 'wrap' }}>
          <Stat label="Total due" value={fmt(sum.total_due)} />
          <Stat label="Paid" value={fmt(sum.total_paid)} color="var(--success)" />
          <Stat label="Pending" value={fmt(sum.total_pending)} color={sum.total_pending > 0 ? 'var(--danger)' : 'var(--success)'} />
        </div>
      )}

      {/* Ledger */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Installment</th>
              <th>Due date</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ textAlign: 'right' }}>Paid</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {installments.map((f) => {
              const overdue = !f.paid && f.due_date && new Date(f.due_date) < new Date();
              return (
                <tr key={f.id}>
                  <td style={{ fontWeight: 600 }}>{f.installment_name}</td>
                  <td className="muted" style={{ textAlign: 'center' }}>{f.due_date ? new Date(f.due_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(f.amount)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(f.paid_amount)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: f.balance > 0 ? 'var(--danger)' : 'var(--text-2)' }}>{fmt(f.balance)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {f.paid
                      ? <span className="badge b-success"><span className="dot" />Paid</span>
                      : f.partial
                        ? <span className="badge b-warning"><span className="dot" />Partial</span>
                        : overdue
                          ? <span className="badge b-danger"><span className="dot" />Overdue</span>
                          : <span className="badge b-neutral"><span className="dot" />Due</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.8px', color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default FeesTab;
